import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuthContext } from '@/features/auth/AuthContext';
import { useT } from '@/i18n/LocaleProvider';

import { createWatchApiClient } from './api';

export type WatchToggleProps = {
  pageID: string;
};

export function WatchToggle({ pageID }: WatchToggleProps) {
  const t = useT();
  const auth = useAuthContext();
  const [watching, setWatching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const client = useMemo(() => createWatchApiClient(() => auth.token), [auth.token]);

  useEffect(() => {
    if (!auth.isAuthenticated || !pageID) {
      return;
    }

    let cancelled = false;

    client.listWatches().then(
      (resp) => {
        if (cancelled) return;
        setWatching(resp.watches.some((w) => w.page_id === pageID));
      },
      () => {
        /* silently ignore load failures for toggle */
      },
    );

    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated, pageID, client]);

  const toggle = useCallback(async () => {
    if (!auth.isAuthenticated) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (watching) {
        await client.unfollowPage(pageID);
        setWatching(false);
      } else {
        await client.followPage(pageID);
        setWatching(true);
      }
    } catch {
      setError(watching ? t('watch_error_unfollow') : t('watch_error_follow'));
    } finally {
      setLoading(false);
    }
  }, [auth.isAuthenticated, watching, client, pageID, t]);

  if (!auth.isAuthenticated) {
    return null;
  }

  return (
    <span className="watch-toggle">
      <button aria-pressed={watching} disabled={loading} type="button" onClick={toggle}>
        {loading ? t('watch_loading') : watching ? t('watch_unfollow') : t('watch_follow')}
      </button>
      {error ? <span className="watch-toggle-error">{error}</span> : null}
    </span>
  );
}
